import React from 'react';
import { ArrowRight, CheckCircle2, Code2, Users2, Laptop2 } from 'lucide-react';
import { motion } from 'framer-motion';

const Features = () => {
  const features = [
    {
      title: "Staff Augmentation",
      description: "Scale your team instantly with pre-vetted engineers. We handle the sourcing, screening, and onboarding so you can focus on building.",
      items: ["Immediate access to top talent", "Flexible engagement models", "Seamless integration with your team"],
      icon: <Users2 className="w-10 h-10 text-blue-600" />,
      image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      reversed: false,
    },
    {
      title: "Project Ownership",
      description: "From concept to delivery, we take full responsibility for your project's success. Our expert teams manage the entire lifecycle.",
      items: ["End-to-end development", "Agile project management", "Guaranteed delivery timelines"],
      icon: <Laptop2 className="w-10 h-10 text-indigo-600" />,
      image: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      reversed: true,
    },
    {
      title: "Dedicated Teams",
      description: "Build a long-term engineering capability with a dedicated team that works exclusively for you, managed by us.",
      items: ["Retain domain knowledge", "Consistent team structure", "Cost-effective scaling"],
      icon: <Code2 className="w-10 h-10 text-purple-600" />,
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      reversed: false,
    }
  ];

  return (
    <section id="services" className="py-32 bg-gray-50 overflow-hidden">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-24">
          <span className="bg-blue-100 text-blue-700 text-sm font-bold px-4 py-2 rounded-full uppercase tracking-wider mb-6 inline-block">
            Our Services
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Comprehensive IT Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Whether you need a single engineer or a complete development team, we have the expertise to deliver.
          </p>
        </div>

        <div className="flex flex-col gap-32">
          {features.map((feature, index) => (
            <motion.div 
              key={index} 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className={`flex flex-col lg:flex-row items-center gap-16 lg:gap-24 ${feature.reversed ? 'lg:flex-row-reverse' : ''}`}
            >
              {/* Text Content */}
              <div className="flex-1 space-y-8">
                <div className="w-20 h-20 p-4 bg-white rounded-2xl shadow-sm inline-flex items-center justify-center border border-gray-100 mb-2">
                  {feature.icon}
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                  {feature.title}
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
                
                <ul className="space-y-4">
                  {feature.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-700 font-medium">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <button className="text-blue-600 font-bold flex items-center gap-2 group hover:gap-3 transition-all">
                  Learn more <ArrowRight className="w-5 h-5" />
                </button>
              </div>

              {/* Image Frame with Gradient */}
              <div className="flex-1 w-full">
                <div 
                  className="p-2 rounded-3xl"
                  style={{
                    background: 'linear-gradient(180deg, #9cc1e7 0%, #eddfd0 50%, #f9f8f8 100%)'
                  }}
                >
                  {/* Inner Card/Image with specific rectangular aspect ratio */}
                  <div 
                    className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-white shadow-sm"
                  >
                    <div className="rounded-xl overflow-hidden w-full h-full relative">
                      <div className="absolute inset-0 bg-black/5 pointer-events-none z-10 mix-blend-overlay"></div>
                      <img 
                        src={feature.image} 
                        alt={feature.title} 
                        className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700 ease-out"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
